import { DataGrid } from '@mui/x-data-grid'
import translate from '../../../i18n/translate'
import Filter from '../Filter'
import ReplayIcon from '@mui/icons-material/Replay'
import { modalsSlice } from '../../../modules/store/reducers/modalsSlice'
import RobotSmsCreditsModal from '../../Modals/RobotSmsCreditsModal'
import useStyles from './useStyles'
import CheckBoxIcon from '@mui/icons-material/CheckBox'
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank'
import { Button, Typography } from '@mui/material'
import CircularProgress from '@mui/material/CircularProgress'
import { Box } from '@mui/system'
import { useEffect, useState } from 'react'
import { userApi } from '../../../modules/services/userService'
import toast, { Toaster } from 'react-hot-toast'
import { useAppDispatch } from '../../../modules/store/hooks'

type Props = {
  type?: string
}

const ProfilesDataGrid = (props: Props) => {
  const defaultPageSize = 10
  const classes = useStyles()
  const dispatch = useAppDispatch()
  const [columns, setColumns] = useState<any[]>([])
  const [isAnyPutRequestLoading, setIsAnyPutRequestLoading] =
    useState<boolean>(false)
  const [rows, setRows] = useState<any[]>([])
  const [count, setCount] = useState<number>(0)
  const [currentCredits, setCurrentCredits] = useState<number>(0)
  const [filterRequestString, setFilterRequestString] = useState<
    string | undefined
  >(undefined)
  const [page, setPage] = useState<number>(0)
  const [pageSize, setPageSize] = useState<number>(defaultPageSize)
  const [changedProfileID, setChangedProfileID] = useState<any | undefined>(
    undefined
  )

  //#region API requests
  const {
    data: usersData,
    isSuccess: isUsersSuccess,
    isFetching: isUsersFetching
  } = userApi.useFetchUsersQuery({
    type: props.type,
    skip: page * pageSize,
    take: pageSize,
    filter: filterRequestString
  })
  const [deleteUser, { isLoading: isDeleteLoading }] =
    userApi.useDeleteUserMutation()
  const [updateUserRobotSmsAccess, { isLoading: isUpdateUserRobotSmsAccess }] =
    userApi.useUpdateRobotSmsAccessMutation()
  const [changeUserAutoConfirmation, { isLoading: isAutoConfirmationLoading }] =
    userApi.useAutoConfirmationMutation()
  const [
    updateUserRobotSmsCredits,
    { isLoading: isUpdateUserRobotSmsCredits }
  ] = userApi.useUpdateRobotSmsCreditsMutation()
  const [restoreUser, { isLoading: isRestoreLoading }] =
    userApi.useRestoreUserMutation()
  const [openAsAdminLazy] = userApi.useLazyOpenUserAsAdminQuery()

  const { toggleRobotSmsCreditsModal } = modalsSlice.actions
  //#endregion

  //#region Handlers
  function onOpenAsAdmin (id: number) {
    openAsAdminLazy({ id: id }).then(response => {
      window.open((response.data as any).link)
    })
  }

  function handleDeleteChange (clickedRow: any) {
    setChangedProfileID(clickedRow.id)

    const updatedUserData = rows.map(x => {
      if (x.id !== clickedRow.id) {
        return x
      } else
        return {
          ...x,
          isDeleted: !x.isDeleted
        }
    })

    ;(!clickedRow.isDeleted
      ? deleteUser(clickedRow.id)
      : restoreUser(clickedRow.id)
    )
      .unwrap()
      .then((result: any) => {
        setRows(updatedUserData)
        setChangedProfileID(undefined)
      })
      .catch((error: any) => console.error('ERROR', error))
  }

  function handleChangeUserRobotSmsAccess (clickedRow: any) {
    setChangedProfileID(clickedRow.id)

    const updatedUserData = rows.map((x: any) => {
      if (x.id === clickedRow.id) {
        return {
          ...x,
          isCanUseRobotSms: !x.isCanUseRobotSms
        }
      }

      return x
    })

    updateUserRobotSmsAccess({
      id: clickedRow.id,
      isCanUseRobotSms: !clickedRow.isCanUseRobotSms
    })
      .unwrap()
      .then((result: any) => {
        setRows(updatedUserData)
        setChangedProfileID(undefined)
      })
      .catch((error: any) => console.error('ERROR', error))
  }

  function handleChangeUserAutoConfirmation (clickedRow: any) {
    setChangedProfileID(clickedRow.id)

    const updatedUserData = rows.map((x: any) => {
      if (x.id === clickedRow.id) {
        return {
          ...x,
          isAutoConfirmation: !x.isAutoConfirmation
        }
      }

      return x
    })

    changeUserAutoConfirmation(clickedRow.id)
      .unwrap()
      .then((result: any) => {
        setRows(updatedUserData)
        setChangedProfileID(undefined)
      })
      .catch((error: any) => console.error('ERROR', error))
  }

  function handleChangeUserRobotSmsCredits (credits: number) {
    const updatedUserData = rows.map((x: any) => {
      if (x.id === changedProfileID) {
        return {
          ...x,
          robotSmsCredits: credits
        }
      }

      return x
    })

    updateUserRobotSmsCredits({
      id: changedProfileID,
      robotSmsCredits: credits
    })
      .unwrap()
      .then((result: any) => {
        setRows(updatedUserData)
        setChangedProfileID(undefined)
        setCurrentCredits(0)
      })
      .catch((error: any) => toast.error(translate('updateCreditsError')))
  }

  function handleOpenChangeUserRobotSmsCreditsModal (
    id: number | string,
    credits: number
  ) {
    setChangedProfileID(id)
    setCurrentCredits(credits)

    dispatch(toggleRobotSmsCreditsModal())
  }

  function handleRestoreChange (clickedRow: any) {
    setChangedProfileID(clickedRow.id)

    const updatedEventData = rows.map(x => {
      if (x.id !== clickedRow.id) {
        return x
      } else
        return {
          ...x,
          isDeleted: false
        }
    })

    restoreUser(clickedRow.id)
      .unwrap()
      .then((result: any) => {
        setRows(updatedEventData.filter(item => item.isDeleted === true))
        setCount(count => (count = count - 1))
        setChangedProfileID(undefined)
      })
      .catch((error: any) => console.error('ERROR', error))
  }
  function onFilterClick () {
    setPage(0)
  }
  //#endregion

  //#region UseEffect`s
  useEffect(() => {
    if (isUsersSuccess) {
      setRows(usersData.list)
      setCount(usersData.count)
    }
  }, [usersData, isUsersSuccess])

  useEffect(() => setIsAnyPutRequestLoading(isDeleteLoading), [isDeleteLoading])

  useEffect(() => {
    let selectedColumns = [
      {
        field: 'totalClearances',
        flex: 1,
        filtrable: false,
        sortable: true,
        headerName: translate('gridProfilesTotalClearances')
      },
      {
        field: 'totalPurchases',
        flex: 1,
        filtrable: false,
        sortable: true,
        headerName: translate('gridProfilesTotalPurchases')
      },
      {
        field: 'amountOfPurchasedTickets',
        flex: 1,
        filtrable: false,
        sortable: true,
        headerName: translate('gridProfilesAmountOfPurchasedTickets')
      },
      {
        field: 'amountOfEvents',
        flex: 1,
        filtrable: false,
        sortable: true,
        headerName: translate('gridProfilesAmountOfEvents')
      }
    ]

    selectedColumns.push({
      field: 'producerAndIdentityCardOrHPAndPhoneNumber',
      flex: 1,
      filtrable: false,
      sortable: true,
      headerName: translate(
        'gridProfilesProducerAndIdentityCardOrHPAndPhoneNumber'
      ),
      renderCell: (params: any) => {
        return (
          <>
            <Box className={classes.StatusCell}>
              <Typography>
                <span>{translate('gridProfilesCreatorOfTheEvent')}</span>{' '}
                {params.row.name} {params.row.surname}
              </Typography>
              <Typography>
                <span>{translate('gridProfilesPostalCode')}</span>{' '}
                {params.row.address}
              </Typography>
              <Typography>
                <span>{translate('gridProfilesCellphoneNumber')}</span>{' '}
                {params.row.phone}
              </Typography>
              <Box mt={'10px'}>
                <Button
                  size='small'
                  color='warning'
                  onClick={() => onOpenAsAdmin(params.row.id)}
                >
                  {translate('gridEventsOpenAsOwner')}
                </Button>
              </Box>
              {params.row.isCanUseRobotSms && (
                <Box mt={'10px'}>
                  <Box mt={'5px'}>
                    <Typography fontSize={14}>
                      <span>
                        {translate('gridProfilesChangeRobotSmsCreditsCount')}
                      </span>{' '}
                      {params.row.robotSmsCredits}
                    </Typography>
                  </Box>
                  <Box mt={'5px'}>
                    <Button
                      disabled={
                        isUpdateUserRobotSmsCredits &&
                        params.row.id === changedProfileID
                      }
                      size='small'
                      color='warning'
                      onClick={() =>
                        handleOpenChangeUserRobotSmsCreditsModal(
                          params.row.id,
                          params.row.robotSmsCredits
                        )
                      }
                    >
                      {translate('gridProfilesChangeRobotSmsCredits')}
                    </Button>
                  </Box>
                </Box>
              )}
              <Button
                sx={{
                  color: '#fc7f04'
                }}
                disabled={isUpdateUserRobotSmsAccess}
                endIcon={
                  isUpdateUserRobotSmsAccess &&
                  params.row.id === changedProfileID ? (
                    <CircularProgress size={20} />
                  ) : (params.row.isCanUseRobotSms as boolean) ? (
                    <CheckBoxIcon style={{ marginRight: '10px' }} />
                  ) : (
                    <CheckBoxOutlineBlankIcon style={{ marginRight: '10px' }} />
                  )
                }
                onClick={() => handleChangeUserRobotSmsAccess(params.row)}
              >
                {translate('gridProfilesChangeRobotSmsAccess')}
              </Button>
              <Button
                sx={{
                  color: '#fc7f04'
                }}
                disabled={isAutoConfirmationLoading}
                endIcon={
                  isAutoConfirmationLoading &&
                  params.row.id === changedProfileID ? (
                    <CircularProgress size={20} />
                  ) : (params.row.isAutoConfirmation as boolean) ? (
                    <CheckBoxIcon style={{ marginRight: '10px' }} />
                  ) : (
                    <CheckBoxOutlineBlankIcon style={{ marginRight: '10px' }} />
                  )
                }
                onClick={() => handleChangeUserAutoConfirmation(params.row)}
              >
                {translate('gridProfilesAutoConfirmationUser')}
              </Button>
              {props.type === 'all' && (
                <Button
                  sx={{
                    color: '#fc7f04'
                  }}
                  disabled={isAnyPutRequestLoading}
                  endIcon={
                    isDeleteLoading && params.row.id === changedProfileID ? (
                      <CircularProgress size={20} />
                    ) : (params.row.isDeleted as boolean) ? (
                      <CheckBoxIcon style={{ marginRight: '10px' }} />
                    ) : (
                      <CheckBoxOutlineBlankIcon
                        style={{ marginRight: '10px' }}
                      />
                    )
                  }
                  onClick={() => handleDeleteChange(params.row)}
                >
                  {translate('gridProfilesDeletedUser')}
                </Button>
              )}
              {props.type === 'deleted' && (
                <Button
                  sx={{ color: '#fc7f04' }}
                  disabled={isAnyPutRequestLoading}
                  endIcon={
                    isRestoreLoading && params.row.id === changedProfileID ? (
                      <CircularProgress
                        size={20}
                        style={{ marginRight: '10px' }}
                      />
                    ) : (
                      <ReplayIcon style={{ marginRight: '10px' }} />
                    )
                  }
                  onClick={() => handleRestoreChange(params.row)}
                >
                  {translate('gridProfilesRestoreUser')}
                </Button>
              )}
            </Box>
          </>
        )
      }
    } as any)

    setColumns(selectedColumns)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, isAnyPutRequestLoading])

  useEffect(() => {
    setPage(0)
  }, [props.type])
  //#endregion

  return (
    <>
      <RobotSmsCreditsModal
        handleChange={(credits: number) =>
          handleChangeUserRobotSmsCredits(credits)
        }
        robotSmsCredits={currentCredits}
      />

      <Toaster position='bottom-right' toastOptions={{ duration: 4000 }} />
      <div
        style={{
          position: 'absolute',
          top: '0',
          width: '100%',
          paddingBottom: '70px'
        }}
      >
        <Filter
          setFilterRequestString={setFilterRequestString}
          fetchRequest={onFilterClick}
          onlyPaidField={true}
          sortByNameField={true}
          searchPlaceholderId="filterSearchUsersHint"
        />
        {isUsersFetching && (
          <Box
            sx={{
              height: '400px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center'
            }}
          >
            <CircularProgress color='warning' />
          </Box>
        )}
        {!isUsersFetching && (
        <DataGrid
          sx={{
            backgroundColor: '#ede9e4',
            borderRadius: '0px',
            border: 'none',

            '& .MuiDataGrid-virtualScroller': {
              marginTop: '0 !important'
            },
            '& .MuiDataGrid-main': {
              overflow: 'visible'
            },
            '.MuiDataGrid-columnHeaders': {
              backgroundColor: '#fc7f03',
              position: 'sticky',
              zIndex: 2,
              borderRadius: '0px',
              border: '0px'
            },
            '.MuiDataGrid-columnHeaderTitleContainer': {
              color: '#fff',
              fontSize: '15px',
              padding: '10px',
              display: 'flex',
              alignContent: 'center',
              justifyContent: 'center'
            },
            '.MuiDataGrid-cell': {
              border: '1px solid #fc7f03',
              padding: '5px',
              display: 'flex',
              alignContent: 'center',
              justifyContent: 'center'
            },
            '.MuiDataGrid-footerContainer': {
              border: '1px solid #fc7f03'
            },
            '.MuiDataGrid-footerContainer p': {
              margin: '0px'
            },
            '.MuiDataGrid-columnHeaderTitle': {
              overflow: 'visible',
              lineHeight: '1.43rem',
              whiteSpace: 'normal'
            }
          }}
          rowCount={count}
          rows={rows}
          columns={columns}
          getRowHeight={() => 'auto'}
          disableSelectionOnClick
          pagination
          paginationMode='server'
          page={page}
          onPageChange={(nextPage) => setPage(nextPage)}
          pageSize={pageSize}
          onPageSizeChange={(nextPageSize) => {
            setPageSize(nextPageSize)
            setPage(0)
          }}
          rowsPerPageOptions={[10, 25, 50]}
          autoHeight
          density='comfortable'
          headerHeight={70}
        />
        )}
      </div>
    </>
  )
}

export default ProfilesDataGrid
